
import { Component, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { format } from '@hokify/shared-components/lib/helpers/datehelpers/format';
import { lsTest } from '@hokify/shared-components/lib/helpers/localstorage';
import HokSwitch from '@hokify/shared-components/lib/components/HokSwitch.vue';
import JoblistMixin from '~/mixins/Joblist';
import { joinFilters } from '~/helpers/utility';
import { getCountryByCode } from '~/helpers/countryLookup';
import { saveToRecentJobSearches } from '@hokify/shared-components/lib/helpers/jobsearch';
import { getAlternateUrls } from '~/helpers/metadata';
import type { IRecentJobsearch } from '~/store/types';
import JobDetail from '@hokify/shared-components/lib/components/JobDetail.vue';
import HokifyBenefits from '@hokify/shared-components/lib/components/HokifyBenefits.vue';
import type { IAPILandingPageAdditionalContent } from '@hokify/common';
import SeoFooter, { ISeoFooterData } from '~/components/website/footer/SeoFooter.vue';
import { truncateFct } from '@hokify/shared-components/lib/helpers/truncate';

const landingPageStore = namespace('user/landingpage');

@Component({
	name: 'JobsPage',
	layout: 'plain-user',
	components: {
		BeautifyText: () => import('@hokify/shared-components/lib/components/BeautifyText.vue'),
		JobDetail,
		HokSwitch,
		HokifyBenefits,
		SeoFooter,
		LocationAutocomplete: () =>
			import('@hokify/shared-components/lib/components/LocationAutocomplete.vue'),
		InfoBox: () => import('@hokify/shared-components/lib/components/InfoBox.vue'),
		JobPreFooter: () => import('~/components/website/jobs/JobPreFooter.vue')
	}
})
export default class JobsPage extends Mixins(JoblistMixin) {
	@landingPageStore.State('popularLandingPages')
	popularLandingPages!: IAPILandingPageAdditionalContent[];

	getCountryByCode = getCountryByCode;

	currentYear = new Date().getFullYear();

	recentJobSearches: IRecentJobsearch[] = [];

	useNewVersion = false;

	truncateFct = truncateFct;

	jobCategories: { name: string; link: string }[] = [
		{ name: 'Geringfügig', link: '/jobs/k/geringfügig' },
		{ name: 'Teilzeit', link: '/jobs/k/teilzeit' },
		{ name: 'Vollzeit', link: '/jobs/k/vollzeit' }
	];

	setJobalarmTriggered() {
		if (!this.jobalarmTriggered && this.enableNotifications) {
			this.activateJobalarm('floating-cta-list');
		}
		this.jobalarmTriggered = !this.jobalarmTriggered;
	}

	updateJobField(
		searchTerm: string,
		_event,
		parameters?: { term: string; type: string; data?: { alias?: string } }
	) {
		if (parameters?.type === 'company' && parameters?.data?.alias) {
			this.$router.push({ path: `/c/${parameters.data.alias}` });
		} else {
			this.currentJobField = searchTerm;
		}
	}

	async mounted() {
		window.addEventListener('scroll', this.handleScroll);
		if (
			!this.$route.path.split('?')[0].endsWith('/jobs') &&
			this.apiData?.beautifiedUrl &&
			!this.$route.path.includes('/f/') &&
			!this.$route.path.includes('/c/')
		) {
			const newSearch = {
				linkUrl: decodeURI(this.apiData?.beautifiedUrl),
				title: this.recentJobSearchTitle(),
				recordCount: this.apiData?.relationCount
			};
			this.recentJobSearches = saveToRecentJobSearches(newSearch);
		}
		if (lsTest()) {
			const storage = localStorage.getItem('recent-jobsearches');
			if (typeof storage === 'string') {
				this.recentJobSearches = JSON.parse(storage);
			}
		}
		if (this.$isMobile.any && !this.enableNotifications && this.apiData?.path?.path) {
			this.$trackWebsiteEvent('web_to_app_banner_view', {});
		}
		if (!this.loggedIn && this.cvSearchCard) {
			this.$trackWebsiteEvent('view_search_by_cv:', { elementContent: this.cvSearchCard.id });
		}
	}

	recentJobSearchTitle(): string {
		let jobSearchTitle = '';
		jobSearchTitle += this.initialJobField ? this.initialJobField : 'Jobs';
		if (this.initialLocationName) {
			jobSearchTitle += ` in ${this.initialLocationName}`;
		}
		return jobSearchTitle;
	}

	setupSendJob(eventLabel = 'cta-text-right') {
		this.$trackWebsiteEvent('send_job', {
			pageElement: eventLabel
		});
	}

	goToApp(): void {
		this.$trackWebsiteEvent('click_get_app', {});
		window.open(this.appstoreLink);
	}

	alternativeCountry(alternative: {
		url: string;
		countryCode?: string;
		city?: string;
		branch?: string;
	}): string {
		if (alternative.countryCode) {
			return (
				getAlternateUrls(this.$store.state.host, [alternative.countryCode])[0].href +
				alternative.url
			);
		}

		return alternative.url;
	}

	alternativeLinkText(alternative: {
		url: string;
		countryCode?: string;
		city?: string;
		branch?: string;
	}): string {
		let result = '';
		if (alternative.branch) {
			result += `${alternative.branch} `;
		}
		result += 'Jobs';

		if (alternative.city) {
			result += ` in ${alternative.city}`;
		}
		if (alternative.countryCode) {
			if (!alternative.city) {
				result += ' in';
			}
			result += ` ${getCountryByCode(alternative.countryCode)}`;
		}

		result += '?';
		return result;
	}

	async activeSourcingInvite(interaction: 'accepted' | 'declined' | 'more-info') {
		this.$trackWebsiteEvent('active_sourcing_reverse_apply', { interaction });
		if (!this.jobDetail?._id || !this.jobDetail?.jobNr) {
			this.$snack.danger({
				text: 'Es ist ein Fehler aufgetreten!'
			});
			this.$router.push('/jobs');
		}
		if (interaction === 'accepted') {
			window.location.href = `/reverse-apply/${this.jobDetail?._id}`;
		} else if (interaction === 'declined') {
			try {
				await this.discardRelation({ type: 'job', obj: this.jobDetail });
				if (this.jobDetail?.selectedCandidateInfo?.status) {
					this.jobDetail.selectedCandidateInfo.status = 'request-rejected';
				}
				this.$snack.success({
					text: 'Das Unternehmen wurde über dein Nicht-Interesse informiert.'
				});
			} catch (err) {
				this.$snack.danger({
					text: 'Entscheidung konnte nicht gespeichert werden!'
				});
			}
		} else if (interaction === 'more-info') {
			window.location.href = `/more-info/${this.jobDetail?.jobNr}`;
		} else {
			throw new Error('interaction type missing');
		}
	}

	get dynamicHeadline(): string {
		return !this.searchBarValue ? 'h1' : 'h2';
	}

	get pageTitle(): string {
		// deactivated until we adapt it at marekting request
		/*	if (this.version === 'lpad') {
			return this.lpadPageTitle;
		} */
		return this.defaultPageTitle;
	}

	get lpadPageTitle(): string {
		const pageTitleParts: string[] = [];

		if (this.apiData) {
			const { branch, city, filters } = this.apiData.selected || {};

			if (branch?.formatted) {
				pageTitleParts.push(`Aktuelle Jobs als ${branch.formatted}`);
			}
			if (city?.formatted) {
				if (branch?.formatted) {
					pageTitleParts.push(`Finde Jobs in ${city.formatted}`);
				} else {
					pageTitleParts.push(`Aktuelle Jobs in ${city.formatted}`);
					pageTitleParts.push(`In 3 Minuten bewerben`);
				}
			} else {
				if (!branch?.formatted) {
					if (filters?.length > 0) {
						pageTitleParts.push(
							`Aktuelle ${joinFilters(
								filters.map(f => f.formatted.replace('in der Branche ', '')),
								', ',
								'und',
								filters.length
							)} Jobs`
						);
					} else {
						pageTitleParts.push(`Aktuelle Jobs`);
					}
				}
				pageTitleParts.push(`Finde Jobs ${getCountryByCode(this.$store.state.topLevelDomain)}`);
			}
		}

		pageTitleParts.push('hokify Job-Plattform');
		return pageTitleParts.join(' | ');
	}

	get defaultPageTitle(): string {
		let pageTitle = '';
		let jobCount;
		if (this.error) {
			pageTitle = 'Jobsuche';
			return pageTitle;
		}

		if (this.apiData) {
			if (this.apiData.relationCount) {
				if (this.apiData.relationCount >= 10000) {
					jobCount = 10000;
				} else if (this.apiData.relationCount >= 1000) {
					jobCount = Math.floor(this.apiData.relationCount / 100) * 100;
				} else if (this.apiData.relationCount >= 10) {
					jobCount = Math.floor(this.apiData.relationCount / 10) * 10;
				} else {
					jobCount = this.apiData.relationCount;
				}
			}
			if (
				this.showJobDetailMeta &&
				this.jobDetail &&
				this.jobDetail.name &&
				this.jobDetail.company &&
				this.jobDetail.company.name &&
				!this.$isMobile.any
			) {
				const jobTitle = this.jobDetail.name.replace('(m/w/d)', '');
				const companyName = this.jobDetail.company.name.replace(/\s*\[\[.*?\]\]/g, '');
				const titleAndCompanyLength = jobTitle.length + companyName.length;

				if (titleAndCompanyLength < 31) {
					pageTitle = `${jobTitle}  ${companyName ? `bei ${companyName}` : ''}  & ähnliche Jobs`;
				} else if (titleAndCompanyLength > 30 && jobTitle.length < 37) {
					pageTitle = `${jobTitle} & weitere Jobs `;
				} else {
					pageTitle = `${jobTitle}`;
				}
			}
			// title without for joblist without splitpage/ fallback when no jobdetail
			// location and keyword title
			else if (this.currentLocationName.length && this.currentJobField.length) {
				const searchtermLength = this.currentLocationName.length + this.currentJobField.length;
				if (searchtermLength > 36) {
					pageTitle = `${this.currentJobField}  Jobs in ${this.currentLocationName} `;
				} else if (searchtermLength > 27 && searchtermLength < 37) {
					pageTitle = `${jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''} ${
						this.currentJobField
					}  Jobs in ${this.currentLocationName} `;
				} else {
					pageTitle = ` ${
						jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount} ` : 'Aktuelle'
					} ${this.currentJobField} Jobs in ${this.currentLocationName}`;
				}
			}
			// location only title
			else if (this.currentLocationName.length) {
				if (this.currentLocationName.length > 19) {
					pageTitle = `${
						jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : 'Aktuelle'
					}  Jobs in ${this.currentLocationName} `;
				} else if (this.currentLocationName.length > 10 && this.currentLocationName.length < 20) {
					pageTitle = `Jobs in ${this.currentLocationName} | In 3 Minuten bewerben `;
				} else {
					pageTitle = `Aktuelle Jobs in ${this.currentLocationName} | In 3 Minuten bewerben`;
				}
				// keyword only title
			} else if (this.currentJobField.length) {
				if (this.currentJobField.length > 23) {
					pageTitle = `${
						jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount} ` : 'Aktuelle'
					} ${this.currentJobField} Jobs`;
				} else if (this.currentJobField.length > 14 && this.currentLocationName.length < 24) {
					pageTitle = `${this.currentJobField} Jobs | ${
						jobCount > 0
							? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount} offene Stellen`
							: 'In 3 Minuten bewerben '
					}`;
				} else {
					pageTitle = `${this.currentJobField} Jobs in deiner Nähe | ${
						jobCount > 0
							? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount} Stellen`
							: 'In 3 Minuten bewerben '
					}`;
				}
			} else {
				pageTitle = `${
					jobCount < 10000 ? `${jobCount}+ ` : jobCount
				} offene Stellen | In 3 Minuten bewerben`;
			}
		}

		return pageTitle;
	}

	get pageDescription(): string {
		// deactivated until we adapt it at marekting request
		/* if (this.version === 'lpad') {
		return this.lpadPageDescription;
	} */
		return this.defaultPageDescription;
	}

	get lpadPageDescription(): string {
		let pageDescriptionPart = '';

		if (this.apiData) {
			const { relationCount } = this.apiData;
			const { branch, city, filters } = this.apiData.selected || {};

			if (!branch?.formatted && !city?.formatted) {
				pageDescriptionPart += 'Aktuelle Jobübersicht von';
				if (filters?.length > 0) {
					pageDescriptionPart += ` ${joinFilters(
						filters.map(f => f.formatted.replace('in der Branche ', '')),
						', ',
						'und',
						filters.length
					)} Jobs ${getCountryByCode(this.$store.state.topLevelDomain)}.`;
				} else {
					pageDescriptionPart += ' vielen Jobangeboten mit Top Gehalt in deiner Umgebung.';
				}
			} else {
				pageDescriptionPart += 'Alle';
				if (relationCount > 1) {
					pageDescriptionPart += ` ${relationCount}`;
				}

				if (branch?.formatted) {
					pageDescriptionPart += ` ${branch.formatted}`;
				}

				pageDescriptionPart += ' Jobs';
				if (city?.formatted) {
					pageDescriptionPart += ` in ${city.formatted}`;
				} else {
					pageDescriptionPart += ` ${getCountryByCode(this.$store.state.topLevelDomain)}`;
				}

				pageDescriptionPart += ' bei hokify finden.';
			}
		}

		pageDescriptionPart +=
			' Große Auswahl & Täglich Aktuell. In 3 Minuten erfolgreich für Jobs ohne Anschreiben bewerben. Mit hokify Profil Jobangebote mit Top Gehalt von führenden Arbeitgebern bekommen.';
		return pageDescriptionPart;
	}

	get defaultPageDescription(): string {
		let pageDescription;
		let jobCount;

		if (this.apiData) {
			if (this.apiData.relationCount) {
				if (this.apiData.relationCount >= 10000) {
					jobCount = 10000;
				} else if (this.apiData.relationCount >= 1000) {
					jobCount = Math.floor(this.apiData.relationCount / 100) * 100;
				} else if (this.apiData.relationCount >= 10) {
					jobCount = Math.floor(this.apiData.relationCount / 10) * 10;
				} else {
					jobCount = this.apiData.relationCount;
				}
			}
			if (
				this.showJobDetailMeta &&
				this.jobDetail &&
				this.jobDetail.name &&
				this.jobDetail.company &&
				this.jobDetail.company.name &&
				!this.$isMobile.any
			) {
				const jobTitle = this.jobDetail.name.replace('(m/w/d)', '');
				const companyName = this.jobDetail.company.name.replace(/\s*\[\[.*?\]\]/g, '');
				const titleAndCompanyLength = jobTitle.length + companyName.length;

				if (titleAndCompanyLength < 31) {
					pageDescription = `${jobTitle} & weitere Jobs bei hokify - deiner mobilen Jobplattform. ✓ Schnelle & einfache Bewerbung ✓ Gratis Lebenslauf ✓ Ohne Anschreiben `;
				} else if (titleAndCompanyLength > 30 && jobTitle.length < 37) {
					pageDescription = `Finde ${jobTitle} Jobs ${
						companyName ? `bei ${companyName}` : ''
					} & anderen Top-Unternehmen mit hokify ${
						this.jobDetail.location && this.jobDetail.location.countryCode === 'AT'
							? '► Wien, Salzburg, Graz, Linz uvm.'
							: '► Berlin, Köln, München, Stuttgart uvm. '
					}  `;
				} else {
					pageDescription = `${jobTitle} Jobs ${
						companyName ? `bei ${companyName}` : ''
					} & weiteren Unternehmen ${
						this.jobDetail.location && this.jobDetail.location.countryCode === 'AT'
							? '► 10.000+ Stellenangebote in Österreich - hokify'
							: '► 10.000+ Stellenangebote in Deutschland - hokify'
					}`;
				}
			}
			// description without for joblist without splitpage / fallbacks when no jobdetail
			// location and keyword title
			else if (this.currentLocationName.length && this.currentJobField.length) {
				const searchtermLength = this.currentLocationName.length + this.currentJobField.length;
				if (searchtermLength > 36) {
					pageDescription = `Finde deinen neuen ${this.currentJobField} Job in ${
						this.currentLocationName
					} mit hokify ✓ Ein Profil für alle Jobs. ${
						jobCount > 0
							? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount} offene Stellen im`
							: '- '
					}  ${this.currDateName}.`;
				} else if (searchtermLength > 27 && searchtermLength < 37) {
					pageDescription = `${
						jobCount > 0
							? `${
									jobCount < 10000 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount} ` : jobCount
								}`
							: ''
					} ${this.currentJobField} Jobs in ${
						this.currentLocationName
					} warten auf dich. ✓ Top Gehalt ✓ mit & ohne Erfahrung ✓ schnelle Bewerbung mit hokify - ${
						this.currDateName
					} `;
				} else {
					pageDescription = `${this.currentJobField}  Job in ${
						this.currentLocationName
					} gesucht?  ${
						jobCount > 0 ? `Entdecke ${jobCount < 10000 ? `${jobCount}+` : jobCount}` : ''
					}  ✓ Vollzeit ✓ Teilzeit ✓ Nebenjobs uvm. bei hokify. Bewirb dich in 3 Minuten & ganz ohne Anschreiben.`;
				}
			}
			// location only description
			else if (this.currentLocationName.length) {
				if (this.currentLocationName.length > 19) {
					pageDescription = `Ein Profil für alle Jobs ► Swipen, chatten, neuer Job - ohne Anschreiben. Entdecke ${
						jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
					} Jobs in ${this.currentLocationName}, die zu dir passen - hokify.`;
				} else if (this.currentLocationName.length > 10 && this.currentLocationName.length < 20) {
					pageDescription = `${
						jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : 'Aktuelle'
					} Stellenangebote in ${
						this.currentLocationName
					}. Ein Profil. Eine Anmeldung. Alle Jobs. Finde jetzt deinen passenden Job mit hokify - ${
						this.currDateName
					} `;
				} else {
					pageDescription = `${
						jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
					} Jobs in ${
						this.currentLocationName
					} verfügbar. Bewirb dich in 3 Minuten ohne Anschreiben - hokify! ✓ Top Gehalt ✓ Home-Office ✓ mit & ohne Erfahrung - ${
						this.currDateName
					}`;
				}
				// keyword only description
			} else if (this.currentJobField.length) {
				if (this.currentJobField.length > 23) {
					pageDescription = `Entdecke ${
						jobCount > 0 ? `über ${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
					} ${this.currentJobField} ${
						jobCount > 0 ? `Stellenangebote` : 'Jobs'
					} in deiner Nähe. ✓ Ein Profil ✓ Eine Anmeldung ✓ Alle Jobs. hokify - deine mobile Jobplattform`;
				} else if (this.currentJobField.length > 14 && this.currentLocationName.length < 24) {
					pageDescription = `${this.currentJobField} Job gesucht? Finde passende Stellenangebote & bewirb dich in 3 Minuten ohne Anschreiben - hokify. ✓ Vollzeit ✓ Teilzeit ✓ Nebenjobs`;
				} else {
					pageDescription = `Neuer Job? ${
						jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
					} ${this.currentJobField} Jobs - ${
						this.currDateName
					}. Swipen, chatten, neuer Job - mit hokify in 3 Minuten bewerben. ✓ Vollzeit ✓ Teilzeit ✓ Lehre uvm. `;
				}
			} else {
				pageDescription = `Neuer Job? Aktuell ${
					jobCount < 10000 ? `${jobCount}+ ` : jobCount
				} Jobs - ${
					this.currDateName
				}. Swipen, chatten, neuer Job - mit hokify in 3 Minuten bewerben. ✓ Vollzeit ✓ Teilzeit ✓ Lehre uvm. `;
			}
		}
		return pageDescription;
	}

	async rowItemClicked(): Promise<void> {
		// set page number here instead of page navigation in case user clicks on multiple jobs on certain page
		this.updateLastJobPageNumber({
			page: this.currentPage,
			beautifiedUrl: this.apiData?.beautifiedUrl
		});
		this.$trackWebsiteEvent('click_job', {});
		this.jobItemClicked(this.apiData?.lpId);
		this.jobgone = false;
		this.resetScrollPosition();
	}

	companyClicked(company) {
		this.$trackWebsiteEvent('companyslot_clicked', {
			_id: company._id,
			name: company.name,
			jobfields: company.fields,
			city: company.location?.[0]?.city || undefined,
			audienceId: company.audienceId
		});
	}

	resetScrollPosition(): void {
		const jobDetailScrollPosition = window?.document?.getElementById('overflow-container');
		if (jobDetailScrollPosition) {
			jobDetailScrollPosition.scrollTop = 0;
		}
	}

	trackCommute(type: 'dropdown' | 'image') {
		this.$trackWebsiteEvent('click_commute_element', {
			pageElement: type
		});
	}

	trackCvSearch() {
		this.$trackWebsiteEvent('start_search_by_cv:', { elementContent: this.cvSearchCard.id });
	}

	get jobTitlePart(): string {
		let jobTitlePart = '';

		if (this.apiData) {
			const { selected } = this.apiData;
			if (selected?.branch?.formatted) {
				jobTitlePart += `${selected.branch.formatted} `;
			}
			jobTitlePart += 'Jobs';
			if (selected?.city?.formatted) {
				jobTitlePart += ` in ${selected.city.formatted}`;
			}
			jobTitlePart += '. ';
			if (!selected?.branch && !selected?.city && selected?.filters?.length > 0) {
				jobTitlePart += `${joinFilters(
					selected.filters.map(f => f.formatted.replace('in der Branche ', '')),
					', ',
					'und',
					selected.filters.length
				)} Jobs. `;
			} else if (
				!selected?.branch &&
				!selected?.city &&
				(!selected?.filters || selected?.filters === 0)
			) {
				jobTitlePart = 'allen Jobs in deiner Umgebung. ';
			}
		}

		return jobTitlePart;
	}

	get currDateName(): string {
		return format('MMMM yyyy', new Date());
	}

	get mappedSeoBranches():
		| {
				title: string;
				description: string;
				to?: string;
		  }[]
		| undefined {
		if (this.popularLandingPages) {
			return this.popularLandingPages.map(page => ({
				title: page.title,
				description: `${page.recordCount}`,
				to: page.linkUrl
			}));
		}
		return undefined;
	}

	get mappedSeoCities():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.otherTerms?.refined) {
			return this.otherTerms?.refined.map(city => ({
				title: city.name,
				description: `${city.results}`,
				to: city.url
			}));
		}
		return undefined;
	}

	get mappedSeoRegions():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.otherTerms?.places) {
			return this.otherTerms.places.map(place => ({
				title: place.name,
				description: `${place.results}`,
				to: place.url
			}));
		}
		return undefined;
	}

	get mappedSeoRecentJobSearches():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.recentJobSearches) {
			return this.recentJobSearches.map(search => ({
				title: search.title,
				description: `${search.recordCount}`,
				to: search.linkUrl
			}));
		}
		return undefined;
	}

	get seoFooterData(): ISeoFooterData[] {
		return [
			{
				title: 'Jobs nach Regionen',
				id: 'regions',
				searches: this.mappedSeoRegions || []
			},
			{
				title: 'Jobs nach Städten',
				id: 'cities',
				searches: this.mappedSeoCities || []
			},
			{
				title: 'Jobs nach Branchen',
				id: 'branches',
				searches: this.mappedSeoBranches || []
			},
			{
				title: 'Deine letzten Suchen',
				id: 'job-searches',
				searches: this.mappedSeoRecentJobSearches || []
			}
		];
	}

	async newSearchViaTag(jobField: string) {
		this.$trackWebsiteEvent('click_jobdetail_tag', {});
		this.currentJobField = jobField;
		await this.handleJobSearch(true);
	}
}
